import React, { useContext } from 'react';

import { ApLoaderDefault, ApLoaderText } from '@alixpartners/ui-components';
import { QueryStringContext } from 'contexts/QueryStringContext';
import { SpendCubeSettingsContext } from 'contexts/SpendCubeSettingsContext';
import { SettingsOptionsContext } from 'contexts/SettingsOptionsContext';
import {
	convertToArray,
	isCustomCategory,
	getCurrencySymbolFromOptions,
} from 'Details/utils';
import { formatSpendWithDecimalAndSymbol } from 'utils/number';
import numeral from 'numeral';
import {
	getTopVendors,
	getInitiativeItemTopVendors,
	getInitiativeTopVendors,
} from 'utils/apiRequests';
import {
	TableWrapper,
	StyledModal,
	HeaderCell,
	HeaderCellNumber,
	Cell,
	CellNumber,
	Row,
	Text,
	Title,
	LoadingContainer,
} from './TopVendorsModal.styles';
const blankName = '(Blank)';

export class TopVendorsModal extends React.Component {
	state = { data: null };

	getApiParams = () => {
		const {
			currency,
			dateFrom,
			dateTo,
			extraFilters,
			storedQueryId,
			showNonSourceable,
			customFiltersValues,
			customFilters,
			includeCustomCategories = false,
			isPmi,
			hierarchy,
			getTailSpendOnly,
			includeTailSpend,
			useNetSpend,
			waterfallFilter,
			isHarmonized,
			isTailSpend,
		} = this.props;
		const params = {
			currencyType: 'fixed',
			useNetSpend,
			currency,
			dateFrom,
			dateTo,
			includeCustomCategories,
			getTailSpendOnly: getTailSpendOnly || isTailSpend,
			includeTailSpend,
		};

		// Sourceable
		params.filters = ['sourceable'];
		params.filterValues = [showNonSourceable !== 'true'];

		if (!isPmi) {
			params.harmonized = isHarmonized;
			params.includeCustomCategories = includeCustomCategories;
			// Analysis waterfall non hierarchical selection (vendor, cost center)
			params.filters = [
				...params.filters,
				...(waterfallFilter ? [waterfallFilter[0]] : []),
			];
			params.filterValues = [
				...params.filterValues,
				...(waterfallFilter ? [waterfallFilter[1]] : []),
			];
		} else {
			params.harmonized = true;
		}

		// Custom filters
		if (customFiltersValues.length > 0) {
			params.filters = [...params.filters, ...customFilters];
			params.filterValues = [...params.filterValues, ...customFiltersValues];
		}

		// Line item filters
		if (extraFilters) {
			const filterObj = Object.keys(extraFilters).reduce(
				(acc, filterName) => {
					return {
						...acc,
						filters: [
							...acc.filters,
							...new Array(extraFilters[filterName].length).fill(filterName),
						],
						filterValues: [...acc.filterValues, ...extraFilters[filterName]],
					};
				},
				{
					filters: [],
					filterValues: [],
				}
			);

			params.filters = [...params.filters, ...filterObj.filters];
			params.filterValues = [...params.filterValues, ...filterObj.filterValues];
		}

		// Hierarchy of categories
		if (hierarchy) {
			params.hierarchy = hierarchy;
		}

		// Stored query param
		if (storedQueryId) {
			params.storedQueryId = storedQueryId;
		}

		return params;
	};

	componentDidMount() {
		this.fetchData();
	}

	fetchData = () => {
		const { initiativeParams } = this.props;
		const request = initiativeParams
			? initiativeParams.initiativeId
				? getInitiativeTopVendors(initiativeParams.initiativeId)
				: getInitiativeItemTopVendors(initiativeParams)
			: getTopVendors(this.getApiParams());

		return request.then(data => {
			this.setState({ data });
		});
	};

	spendFormatter = value => {
		const { currencySymbol, hideDecimals } = this.props;

		return value
			? formatSpendWithDecimalAndSymbol(
					value,
					currencySymbol,
					hideDecimals,
					2
			  ).toUpperCase()
			: '-';
	};

	render() {
		const { onClose, name } = this.props;
		const { data } = this.state;

		return (
			<StyledModal
				isOpen={true}
				header="Top vendors"
				isDismissible
				hasClosed={onClose}
			>
				<TableWrapper>
					<Title>{name || blankName}</Title>
					{!data ? (
						<LoadingContainer>
							<ApLoaderDefault loaderText={() => <ApLoaderText />} />
						</LoadingContainer>
					) : (
						<React.Fragment>
							<Row>
								<HeaderCell>
									<Text>Rank</Text>
								</HeaderCell>
								<HeaderCell>
									<Text>Vendor name</Text>
								</HeaderCell>
								<HeaderCellNumber>Spend</HeaderCellNumber>
								<HeaderCellNumber>Transactions</HeaderCellNumber>
							</Row>
							{data.map(({ vendorName, spend, transactionsCount }, index) => (
								<Row key={index}>
									<Cell>
										<Text bold>{index + 1}</Text>
									</Cell>
									<Cell>
										<Text>{vendorName}</Text>
									</Cell>
									<CellNumber>{this.spendFormatter(spend)}</CellNumber>
									<CellNumber>
										{numeral(transactionsCount).format('0,0')}
									</CellNumber>
								</Row>
							))}
						</React.Fragment>
					)}
				</TableWrapper>
			</StyledModal>
		);
	}
}

export default props => {
	const {
		currency,
		customFilters,
		customFiltersValues,
		waterfallFilter,
		dateFrom,
		dateTo,
		measures,
		showNonSourceable,
		dimensions,
		storedQueryId,
		showHarmonizedVendors,
		isTailSpend,
		hideDecimals,
	} = useContext(QueryStringContext);
	const {
		options: { currency: currencyList },
	} = useContext(SettingsOptionsContext);
	const { settings } = useContext(SpendCubeSettingsContext);
	const currencySymbol = getCurrencySymbolFromOptions(currencyList, currency);
	const useNetSpend =
		settings && settings.tailSpend ? settings.tailSpend.useNetSpend : true;

	return (
		<TopVendorsModal
			dateFrom={dateFrom}
			dimensions={dimensions}
			dateTo={dateTo}
			customFilters={convertToArray(customFilters)}
			customFiltersValues={convertToArray(customFiltersValues)}
			waterfallFilter={waterfallFilter}
			hierarchy={measures}
			hideDecimals={hideDecimals}
			isHarmonized={showHarmonizedVendors === 'true'}
			isTailSpend={isTailSpend === 'true'}
			showNonSourceable={showNonSourceable}
			includeCustomCategories={isCustomCategory(dimensions)}
			storedQueryId={storedQueryId}
			currencySymbol={currencySymbol}
			currency={currency}
			useNetSpend={useNetSpend}
			{...props}
		/>
	);
};
